import React from 'react';
import i18n from 'i18n-js';
import moment from 'moment';
import { View } from 'react-native';
import { convertTime } from '@helpers';
import Image from '@v2/context/Image/context';
import { DATA_GRID_IMAGE_COLUMN_WIDTH, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { LevelSubScreen } from '@v2/pages/common/enums';
import { NCFileDataGridColumnsParams } from './types';

export const getNCFileDataGridColumns = ({ scale, goToDetails, attachMode = false }: NCFileDataGridColumnsParams) => [
  {
    field: 'Image',
    headerName: '',
    align: 'center',
    width: scale(DATA_GRID_IMAGE_COLUMN_WIDTH),
    sortable: false,
    filterable: false,
    renderCell: ({ row }: { row: any }) => {
      const imgSize = { width: scale(50), height: scale(30) };
      const imageData = {
        image: {
          documentId: row.imageDocumentId,
          imageHash: row.imageHash,
          imageData: {
            isCropped: row.imageData?.isCropped,
            extensionDerivative: row.imageData?.extensionDerivative,
            pageNumber: row.imageData?.pageNumber,
            rotationAngle: row.imageData?.rotationAngle || 0,
            rotationAngleCrop: row.imageData?.rotationAngle || 0,
          },
          width: imgSize.width,
          height: imgSize.height,
          contextId: row.ncFileId,
          contextCode: 'c',
        },
        onPress: () => goToDetails(row, LevelSubScreen.DOCUMENTS),
      };
      return (
        <View testID={TEST_IDS.searchScreen.ncFiles.dataGridImage} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
          <Image {...imageData} attachMode={attachMode} />
        </View>
      );
    },
  },
  {
    field: 'ncFileName',
    headerName: i18n.t('NC File'),
    align: 'left',
    width: scale(150),
    sortable: true,
    filterable: true,
  },
  {
    field: 'runTimePerPcsSecond',
    headerName: i18n.t('Duration Active'),
    align: 'left',
    width: scale(150),
    sortable: true,
    filterable: true,
    valueFormatter: (value: number) => (value ? convertTime(value) : ''),
  },
  {
    field: 'changeTimePerPcsSecond',
    headerName: i18n.t('Duration Change'),
    align: 'left',
    width: scale(150),
    sortable: true,
    filterable: true,
    valueFormatter: (value: number) => (value ? convertTime(value) : ''),
  },
  {
    field: 'setupTimeSecond',
    headerName: i18n.t('Duration Setup'),
    align: 'left',
    width: scale(150),
    sortable: true,
    filterable: true,
    valueFormatter: (value: number) => (value ? convertTime(value) : ''),
  },
  {
    field: 'createdOn',
    headerName: i18n.t('Date Created'),
    align: 'left',
    width: scale(150),
    type: 'date',
    sortable: true,
    filterable: true,
    valueGetter: (value: string) => (value ? new Date(value) : value),
    valueFormatter: (value: Date) => (value ? moment(value).format('DD/MM/YYYY') : ''),
  },
  {
    field: 'firstRanByMachineName',
    headerName: i18n.t('First Machine'),
    align: 'left',
    width: scale(150),
    sortable: true,
    filterable: true,
  },
  {
    field: 'firstRanOn',
    headerName: i18n.t('Date First Active'),
    align: 'left',
    width: scale(150),
    type: 'date',
    sortable: true,
    filterable: true,
    valueGetter: (value: string) => (value ? new Date(value) : value),
    valueFormatter: (value: Date) => (value ? moment(value).format('DD/MM/YYYY') : ''),
  },
  {
    field: 'lastRanByMachineName',
    headerName: i18n.t('Last Machine'),
    align: 'left',
    width: scale(150),
    sortable: true,
    filterable: true,
  },
  {
    field: 'lastRanOn',
    headerName: i18n.t('Date Last Active'),
    align: 'left',
    width: scale(150),
    type: 'date',
    sortable: true,
    filterable: true,
    valueGetter: (value: string) => (value ? new Date(value) : value),
    valueFormatter: (value: Date) => (value ? moment(value).format('DD/MM/YYYY') : ''),
  },
  {
    field: 'linkedBy',
    headerName: i18n.t('Linked By'),
    align: 'left',
    width: scale(150),
    sortable: true,
    filterable: true,
  },
  {
    field: 'linkedOn',
    headerName: i18n.t('Date First Linked'),
    align: 'left',
    width: scale(150),
    type: 'date',
    sortable: true,
    filterable: true,
    valueGetter: (value: string) => (value ? new Date(value) : value),
    valueFormatter: (value: Date) => (value ? moment(value).format('DD/MM/YYYY') : ''),
  },
  {
    field: 'linksSummary',
    headerName: i18n.t('Links'),
    align: 'left',
    width: scale(150),
    sortable: true,
    filterable: true,
  },
  {
    field: 'memo',
    headerName: i18n.t('Memo'),
    align: 'left',
    width: scale(150),
    sortable: true,
    filterable: true,
  },
  {
    field: 'ncFileStatus',
    headerName: i18n.t('Type'),
    align: 'left',
    width: scale(150),
    sortable: true,
    filterable: true,
  },
  {
    field: 'partDescription',
    headerName: i18n.t('Part Description'),
    align: 'left',
    width: scale(150),
    sortable: true,
    filterable: true,
  },
  { field: 'partDrawingNumber', headerName: i18n.t('Drawing'), align: 'left', width: scale(150), sortable: true, filterable: true },
  { field: 'partDrawingPosition', headerName: i18n.t('Drawing Position'), align: 'left', width: scale(150), sortable: true, filterable: true },
  { field: 'partDrawingRevision', headerName: i18n.t('Drawing Revision'), align: 'left', width: scale(150), sortable: true, filterable: true },
  { field: 'partNumber', headerName: i18n.t('Part'), align: 'left', width: scale(150), sortable: true, filterable: true },
  { field: 'setupSheet', headerName: i18n.t('Setup Sheet'), align: 'left', width: scale(150), sortable: true, filterable: true },
];
